import React, {useCallback, useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {Button, Confirm, List, ListItem, Modal, ModalActions, ModalContent, ModalHeader} from "semantic-ui-react";
import '../styles/companies.scss'
import SuppliersStep from "./SuppliersStep";
import CheckStep from "./CheckStep";
import CreateTenderModal from "../CreateTenderModal";
import reduxConnect from "../../reduxConnect";
import {useLocalStorage} from "../../../../components/App/LocalStorageContext";
import {REQUEST_CREATED_KEY, RL_SELECTED_KEY} from "../utils";
import {history} from "../../../../store";
import {requestManager} from "../../requestManager";
import {actions as rlActions} from "../../redux";
import {actions as rfqActions} from "../../rfqView/redux";

import get from "lodash/get";

import {withContext} from "components/App/Context";
import {canEdit} from "components/App/permissions";

const CompaniesModal = (
  {
    createRequest,
    match: { params: {id} },
    context: { user = {} },
  }) => {

  const dispatch = useDispatch();

  const [request, setRequest] = useState(null);
  // Local state for suppliers - loaded from server, not localStorage
  const [suppliers, setSuppliers] = useState([]);
  // FIN-1102 / SELLWISOR-93: per-supplier quoting stats (latency + decliner flags)
  const [supplierStats, setSupplierStats] = useState({});
  // SELLWISOR-93: decliners awaiting the "Send anyway / Exclude" decision
  const [declinerWarning, setDeclinerWarning] = useState(null);

  const [selectedItems, setSelectedItems] = useLocalStorage(RL_SELECTED_KEY, []);
  const [, setCreatedRequest] = useLocalStorage(REQUEST_CREATED_KEY, null);

  const [message, setMessage] = useState('')
  const [disabled, setDisabled] = useState(false);
  const [confirmResend, setConfirmResend] = useState(false);
  const [showTenderModal, setShowTenderModal] = useState(false);

  // wizard steps:
  // 0 - select suppliers
  // 1 - check and send request
  const [currentStep, setCurrentStep] = useState(0);

  const handleClose = useCallback((e) => {
    e.preventDefault();
    // Refresh RL and RFQ views
    dispatch(rlActions.loadWithDefaultFilter());
    dispatch(rfqActions.loadRfqList());
    history.push('/requisition_lists');
  }, [dispatch]);

  const handlePrevStep = useCallback(() => {
    setCurrentStep(prev => prev - 1);
  }, []);

  const sendRequest = useCallback(() => {
    setDisabled(true);
    const onSuccess = () => {
      setCreatedRequest(null)
      setSelectedItems([]);
      history.push('/requisition_lists');
    }
    const payload = {
      message,
      status: 'active'
    }
    requestManager().updateRequest(id, payload, onSuccess);
    setTimeout(() => setDisabled(false), 2000);
  }, [message, id, setCreatedRequest, setSelectedItems]);

  // SELLWISOR-93: suppliers with 4+ negative outcomes on the request's
  // equipment that are not excluded yet
  const findDecliners = useCallback(() => {
    const excludedIds = (get(request, 'attributes.excluded_supplier_ids', []) || []).map(String);
    return suppliers.filter((supplier) => {
      const stats = supplierStats[String(supplier.id)];
      return stats?.decline_threshold_exceeded && !excludedIds.includes(String(supplier.id));
    });
  }, [request, suppliers, supplierStats]);

  const handleNextStep = useCallback(() => {
    const newStep = currentStep + 1;
    if (newStep === 2) {
      const decliners = findDecliners();
      if (decliners.length) {
        setDeclinerWarning(decliners);
        return;
      }
      sendRequest();
    } else {
      setCurrentStep(newStep);
    }
  }, [currentStep, findDecliners, sendRequest]);

  const handleSendAnyway = useCallback(() => {
    setDeclinerWarning(null);
    sendRequest();
  }, [sendRequest]);

  const handleExcludeAndSend = useCallback(() => {
    const supplierIds = (declinerWarning || []).map(({id: supplierId}) => supplierId);
    setDeclinerWarning(null);
    requestManager().excludeSupplierFromRequest(request, supplierIds, sendRequest);
  }, [declinerWarning, request, sendRequest]);

  // Maps declined RL ids to their visible numbers for the warning dialog
  const declinedRlLabel = useCallback((rlIds = []) => {
    const numbers = rlIds.map((rlId) => {
      const rl = selectedItems.find(({id: itemId}) => itemId === rlId);
      return rl ? `#${get(rl, 'attributes.number') || get(rl, 'attributes.full_number')}` : null;
    }).filter(Boolean);
    return numbers.length ? ` in RL ${numbers.join(', ')}` : '';
  }, [selectedItems]);

  const handleResendClick = useCallback(() => {
    setConfirmResend(true);
  }, []);

  const handleResendCancel = useCallback(() => {
    setConfirmResend(false);
  }, []);

  const handleResendConfirm = useCallback(() => {
    setConfirmResend(false);
    setDisabled(true);
    const onSuccess = () => {
      setCreatedRequest(null);
      setSelectedItems([]);
      history.push('/requisition_lists');
    };
    requestManager().resendRequest(id, message, onSuccess);
    setTimeout(() => setDisabled(false), 2000);
  }, [id, message, setCreatedRequest, setSelectedItems]);

  const handleOpenTenderModal = useCallback(() => {
    setShowTenderModal(true);
  }, []);

  const handleCloseTenderModal = useCallback(() => {
    setShowTenderModal(false);
  }, []);

  // Reload request data from server (to get updated supplier_ids)
  const reloadRequest = useCallback(() => {
    console.log("reloadRequest", id);
    if (!id) return;
    requestManager().loadRequests(id, (data) => {
      setRequest(data);
      const supplierIds = get(data, 'attributes.supplier_ids', []);
      requestManager().loadSuppliers(supplierIds, setSuppliers);
      requestManager().loadSupplierStats(id, setSupplierStats);
    });
  }, [id]);

  // Load suppliers from server when entering CheckStep
  useEffect(() => {
    console.log("useEffect currentStep", currentStep, id);
    if (currentStep === 1 && id) {
      requestManager().loadRequests(id, (data) => {
        setRequest(data);
        const supplierIds = get(data, 'attributes.supplier_ids', []);
        requestManager().loadSuppliers(supplierIds, setSuppliers);
        requestManager().loadSupplierStats(id, setSupplierStats);
      });
    }
  }, [currentStep, id]);

  useEffect(() => {
    if (!id) return;

    const onSuccess = (data) => {
      setRequest(data);
      setCreatedRequest(data);
      requestManager().loadRequisitionLists(data.attributes.requisition_list_ids, setSelectedItems);
      const supplierIds = get(data, 'attributes.supplier_ids', []);
      requestManager().loadSuppliers(supplierIds, (loadedSuppliers) => {
        setSuppliers(loadedSuppliers);
        if(loadedSuppliers.length) {
          setCurrentStep(1);
        }
      });
    }
    requestManager().loadRequests(id, onSuccess)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const role = get(user, 'role', '');
  const canRLEdit = canEdit("requisition_lists", role);
  const canCompanyEdit = canEdit("companies", role);
  const isActive = get(request, 'attributes.status') === 'active';

  return (
    <Modal
      size='fullscreen'
      open
      className="CompaniesModal"
    >
      {currentStep === 0 && <SuppliersStep request={request}/>}
      {currentStep === 1 && <CheckStep request={request} message={message} setMessage={setMessage} canRLEdit={canRLEdit} canCompanyEdit={canCompanyEdit} suppliers={suppliers} supplierStats={supplierStats} onSuppliersChange={reloadRequest}/>}
      <ModalActions>
        <Button primary onClick={handleClose}>
          Save & Close
        </Button>
        {canRLEdit && currentStep > 0 &&
          <Button color="olive" onClick={handlePrevStep}>
            Previous
          </Button>
        }
        {canRLEdit && currentStep === 1 && isActive &&
          <Button color="orange" onClick={handleResendClick} disabled={disabled} loading={disabled}>
            Resend
          </Button>
        }
        {canRLEdit && currentStep === 1 && isActive && selectedItems.length > 0 &&
          <Button color="teal" onClick={handleOpenTenderModal}>
            Create Tender
          </Button>
        }
        {canRLEdit &&
        <Button positive onClick={handleNextStep} disabled={disabled} loading={disabled}>
          {currentStep === 1 ? 'Send' : 'Next'}
        </Button>
        }
      </ModalActions>
      <Confirm
        open={confirmResend}
        onCancel={handleResendCancel}
        onConfirm={handleResendConfirm}
        header="Resend Quotations"
        content="Quotations with 'New' status will receive a reminder with an updated message. For all other statuses, new quotations will be created. Do you want to continue?"
        confirmButton="Resend"
        cancelButton="Cancel"
      />
      {/* SELLWISOR-93: pre-send decliner warning — Send anyway or exclude */}
      <Modal size="small" open={!!declinerWarning}>
        <ModalHeader>Suppliers that keep declining this equipment</ModalHeader>
        <ModalContent>
          <p>These companies never quote such equipment (4+ negative outcomes over the last 12 months):</p>
          <List bulleted>
            {(declinerWarning || []).map((supplier) => {
              const stats = supplierStats[String(supplier.id)] || {};
              return (
                <ListItem key={`decliner-${supplier.id}`}>
                  <b>{get(supplier, 'attributes.short_name')}</b>
                  {declinedRlLabel(stats.declined_requisition_list_ids)}
                </ListItem>
              );
            })}
          </List>
          <p>Excluded companies stay on the request with the "excluded" status and will not receive a quotation.</p>
        </ModalContent>
        <ModalActions>
          <Button color="orange" onClick={handleSendAnyway} disabled={disabled}>
            Send anyway
          </Button>
          <Button positive onClick={handleExcludeAndSend} disabled={disabled}>
            Exclude from request & Send
          </Button>
        </ModalActions>
      </Modal>
      <CreateTenderModal
        open={showTenderModal}
        onClose={handleCloseTenderModal}
        requisitionLists={selectedItems}
        requestId={id}
      />
    </Modal>
  );
}

export default withContext(reduxConnect(CompaniesModal));
